import makerjs from 'makerjs'
import Decimal from 'decimal.js'

/**
 * 生成带圆角的矩形路径（外框或内框）
 * @param {number} left
 * @param {number} top
 * @param {number} right
 * @param {number} bottom
 * @param {number} radius
 * @param {string} prefix 路径名前缀
 */
function roundedRectPaths(left, top, right, bottom, radius, prefix = '') {
    const width = right - left
    const height = bottom - top
    const paths = {}
    if (radius > 0) {
        const r = Math.min(radius, width / 2, height / 2)
        paths[`${prefix}top`] = new makerjs.paths.Line([left + r, top], [right - r, top])
        paths[`${prefix}right`] = new makerjs.paths.Line([right, top + r], [right, bottom - r])
        paths[`${prefix}bottom`] = new makerjs.paths.Line([right - r, bottom], [left + r, bottom])
        paths[`${prefix}left`] = new makerjs.paths.Line([left, bottom - r], [left, top + r])
        paths[`${prefix}arc_tr`] = new makerjs.paths.Arc([right - r, top + r], r, 270, 360)
        paths[`${prefix}arc_br`] = new makerjs.paths.Arc([right - r, bottom - r], r, 0, 90)
        paths[`${prefix}arc_bl`] = new makerjs.paths.Arc([left + r, bottom - r], r, 90, 180)
        paths[`${prefix}arc_tl`] = new makerjs.paths.Arc([left + r, top + r], r, 180, 270)
    } else {
        paths[`${prefix}top`] = new makerjs.paths.Line([left, top], [right, top])
        paths[`${prefix}right`] = new makerjs.paths.Line([right, top], [right, bottom])
        paths[`${prefix}bottom`] = new makerjs.paths.Line([right, bottom], [left, bottom])
        paths[`${prefix}left`] = new makerjs.paths.Line([left, bottom], [left, top])
    }
    return paths
}

/**
 * 对一条闭合 chain 做圆角（最后处理）
 * makerjs.chain.fillet 会就地缩短 chain 中的线段端点，并返回包含圆弧的模型；
 * 再与缩短后的线段合并成完整圆角轮廓。
 * @returns {Object} 带圆角的路径模型
 */
function filletChainContour(chain, radius) {
    if (!chain) return { paths: {} }
    if (!radius || radius <= 0) {
        return makerjs.chain.toNewModel(chain)
    }
    try {
        // 1) 对 chain 各拐角做 fillet（就地修改线段端点，返回圆弧集合）
        const filletsModel = makerjs.chain.fillet(chain, radius)
        // 2) 缩短后的线段转成模型
        const contour = makerjs.chain.toNewModel(chain)
        if (!filletsModel) return contour

        // 3) 把圆弧路径并入同一模型（官方推荐：挂到 models 下即可被 exporter 识别）
        if (!contour.models) contour.models = {}
        contour.models['fillets'] = filletsModel
        return contour
    } catch (e) {
        console.log('chain 圆角失败，回退直角', e)
        try {
            return makerjs.chain.toNewModel(chain)
        } catch (e2) {
            return { paths: {} }
        }
    }
}

/**
 * 生成外壳三层结构
 * @param {Array} keysArray - 按键数据数组
 * @param {Object} options - 配置选项
 * @returns {Object} - 包含 layer1, layer2, layer3 的模型对象
 */
export function buildCase(keysArray, options) {
    const {
        unitWidth = new Decimal(19.05),
        unitHeight = new Decimal(19.05),
        borderTop = new Decimal(5),
        borderBottom = new Decimal(5),
        borderLeft = new Decimal(5),
        borderRight = new Decimal(5),
        boardRadius = new Decimal(2), // 外框圆角半径（使用全局板框圆角）
        innerRadius = new Decimal(1), // 内框圆角半径（层1挖孔轮廓 + 层2内框）
    } = options

    // 计算定位板边界
    let minX = new Decimal(Number.POSITIVE_INFINITY)
    let minY = new Decimal(Number.POSITIVE_INFINITY)
    let maxX = new Decimal(Number.NEGATIVE_INFINITY)
    let maxY = new Decimal(Number.NEGATIVE_INFINITY)

    for (const key of keysArray) {
        const halfW = key.width.times(unitWidth).times(0.5).toNumber()
        const halfH = key.height.times(unitHeight).times(0.5).toNumber()
        const ox = key.centerX.times(unitWidth).toNumber()
        const oy = key.centerY.times(unitHeight).toNumber()
        const rad = key.angle.times(-1).toNumber() * Math.PI / 180
        const cosA = Math.cos(rad)
        const sinA = Math.sin(rad)
        const corners = [
            [ox + (-halfW * cosA - (-halfH) * sinA), oy + (-halfW * sinA + (-halfH) * cosA)],
            [ox + (halfW * cosA - (-halfH) * sinA), oy + (halfW * sinA + (-halfH) * cosA)],
            [ox + (halfW * cosA - halfH * sinA), oy + (halfW * sinA + halfH * cosA)],
            [ox + (-halfW * cosA - halfH * sinA), oy + (-halfW * sinA + halfH * cosA)],
        ]
        for (const [cx, cy] of corners) {
            if (cx < minX.toNumber()) minX = new Decimal(cx)
            if (cx > maxX.toNumber()) maxX = new Decimal(cx)
            if (cy < minY.toNumber()) minY = new Decimal(cy)
            if (cy > maxY.toNumber()) maxY = new Decimal(cy)
        }
    }

    // 定位板尺寸（和 PlateBuilder 保持一致的坐标转换）
    const plateLeft = minX.toNumber()
    const plateRight = maxX.toNumber()
    const plateTop = maxY.times(-1).toNumber() // 物理顶部（翻转后为较小的Y值）
    const plateBottom = minY.toNumber() // 物理底部（翻转后为较大的Y值）
    const plateWidth = plateRight - plateLeft
    const plateHeight = plateBottom - plateTop // 高度 = 底部 - 顶部

    // 外壳尺寸（根据定位板扩展开关）
    const borderTopNum = borderTop.toNumber()
    const borderBottomNum = borderBottom.toNumber()
    const borderLeftNum = borderLeft.toNumber()
    const borderRightNum = borderRight.toNumber()

    // 外壳始终向外扩展边框宽度
    const caseLeft = plateLeft - borderLeftNum
    const caseRight = plateRight + borderRightNum
    const caseTop = plateTop - borderTopNum
    const caseBottom = plateBottom + borderBottomNum

    const caseWidth = caseRight - caseLeft
    const caseHeight = caseBottom - caseTop
    const boardRadiusNum = boardRadius.toNumber()
    const innerRadiusNum = innerRadius.toNumber()

    // ========== 层1：三明治堆叠壳最上层，按照按键键帽尺寸生成一圈挖孔 ==========
    const layer1 = { models: {}, paths: {} }

    // 层1外框（和外壳尺寸一样，带圆角，使用板框圆角）
    Object.assign(layer1.paths, roundedRectPaths(caseLeft, caseTop, caseRight, caseBottom, boardRadiusNum, 'outer_'))

    // ========== 层1：合并按键孔后提取连续轮廓，最后做内框圆角 ==========
    let combinedHoles = null
    for (const key of keysArray) {
        const keyWidth = key.width.times(unitWidth).toNumber()
        const keyHeight = key.height.times(unitHeight).toNumber()
        const cx = key.centerX.times(unitWidth).toNumber()
        const cy = key.centerY.times(unitHeight).times(-1).toNumber()
        const angle = key.angle.times(-1).toNumber()

        // 创建直角方形挖孔（以按键中心为原点，向外偏移 0.5mm）
        // 圆角不在此处处理，统一在合并提取轮廓后最后处理
        const halfW = keyWidth / 2 + 0.5
        const halfH = keyHeight / 2 + 0.5
        const hole = { paths: {} }

        hole.paths['top'] = new makerjs.paths.Line([-halfW, -halfH], [halfW, -halfH])
        hole.paths['right'] = new makerjs.paths.Line([halfW, -halfH], [halfW, halfH])
        hole.paths['bottom'] = new makerjs.paths.Line([halfW, halfH], [-halfW, halfH])
        hole.paths['left'] = new makerjs.paths.Line([-halfW, halfH], [-halfW, -halfH])

        // 旋转并移动到正确位置
        const rotatedHole = makerjs.model.rotate(hole, angle, [0, 0])
        rotatedHole.origin = [cx, cy]

        if (combinedHoles === null) {
            combinedHoles = rotatedHole
        } else {
            try {
                combinedHoles = makerjs.model.combineUnion(combinedHoles, rotatedHole)
            } catch (e) {
                console.log('合并按键孔失败', e)
            }
        }
    }

    // 合并完成后：提取连续闭合轮廓 → 最后做圆角
    if (combinedHoles) {
        makerjs.model.originate(combinedHoles)
        try {
            makerjs.model.simplify(combinedHoles)
        } catch (e) {
            // simplify 偶尔在复杂重叠时会抛错，忽略即可
        }

        const chains = makerjs.model.findChains(combinedHoles) || []
        const continuousHoles = { models: {}, paths: {} }
        let holeIdx = 0

        chains.forEach((chain) => {
            // 只保留闭合（endless）轮廓
            if (!chain.endless) return

            // 最后处理：对闭合轮廓做内框圆角
            const contour = filletChainContour(chain, innerRadiusNum)
            continuousHoles.models[`hole_${holeIdx++}`] = contour
        })

        if (Object.keys(continuousHoles.models).length > 0) {
            layer1.models['combinedHoles'] = continuousHoles
        } else {
            // 回退：无法提取 chain 时，尝试对整个合并模型找 chain 再圆角
            // 若仍失败则保持直角
            layer1.models['combinedHoles'] = combinedHoles
        }
    }

    // ========== 层2：外框加限制定位板边框尺寸挖空 ==========
    const layer2 = { models: {}, paths: {} }

    // 层2外框（和外壳尺寸一样，带圆角，使用板框圆角）
    Object.assign(layer2.paths, roundedRectPaths(caseLeft, caseTop, caseRight, caseBottom, boardRadiusNum, 'outer_'))

    // 层2内部挖空（定位板尺寸；内框圆角）
    Object.assign(layer2.paths, roundedRectPaths(plateLeft, plateTop, plateRight, plateBottom, innerRadiusNum, 'inner_'))

    // ========== 层3：最底层就一个外边框 ==========
    const layer3 = { models: {}, paths: {} }

    // 层3外框（和外壳尺寸一样，带圆角，使用板框圆角）
    Object.assign(layer3.paths, roundedRectPaths(caseLeft, caseTop, caseRight, caseBottom, boardRadiusNum, 'outer_'))

    // 计算模型线长（遍历所有路径）
    const calcModelPathLength = (model) => {
        let total = 0
        if (model.paths) {
            for (const p of Object.values(model.paths)) {
                try {
                    total += makerjs.measure.pathLength(p)
                } catch (e) {}
            }
        }
        if (model.models) {
            for (const m of Object.values(model.models)) {
                total += calcModelPathLength(m)
            }
        }
        return total
    }

    const layer1LineLength = calcModelPathLength(layer1)
    const layer2LineLength = calcModelPathLength(layer2)
    const layer3LineLength = calcModelPathLength(layer3)

    return {
        layer1,
        layer2,
        layer3,
        dimensions: {
            width: caseWidth,
            height: caseHeight,
            plateWidth,
            plateHeight,
        },
        layer1Dimensions: {
            width: caseWidth,
            height: caseHeight,
            area: caseWidth * caseHeight,
            totalLength: layer1LineLength,
        },
        layer2Dimensions: {
            width: caseWidth,
            height: caseHeight,
            area: caseWidth * caseHeight,
            totalLength: layer2LineLength,
        },
        layer3Dimensions: {
            width: caseWidth,
            height: caseHeight,
            area: caseWidth * caseHeight,
            totalLength: layer3LineLength,
        },
    }
}
